// src/pages/OrderSuccessPage.js - EPIC Order Confirmation with Real-time Updates
import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate, Link } from 'react-router-dom';

const OrderSuccessPage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [orderData, setOrderData] = useState(null);
  const [countdown, setCountdown] = useState(5);
  const [showConfetti, setShowConfetti] = useState(true);

  useEffect(() => {
    // Get order data from navigation state
    const data = location.state?.orderData;
    
    if (!data) {
      // If no order data, redirect to menu
      navigate('/menu');
      return;
    }

    setOrderData(data);

    // Hide confetti after animation
    const confettiTimer = setTimeout(() => {
      setShowConfetti(false);
    }, 3000);

    return () => clearTimeout(confettiTimer);
  }, [location.state, navigate]);

  // Countdown timer for auto-redirect to tracking
  useEffect(() => {
    if (countdown > 0) {
      const timer = setTimeout(() => {
        setCountdown(countdown - 1);
      }, 1000);
      return () => clearTimeout(timer);
    }
  }, [countdown]);

  // Auto redirect to tracking page
  const handleTrackOrder = () => {
    navigate(`/track/${orderData?.orderId}`);
  };

  const handleContinueShopping = () => {
    navigate('/menu');
  };

  if (!orderData) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh',
        background: 'linear-gradient(135deg, var(--nigeria-green), var(--warm-orange))'
      }}>
        <div style={{
          background: 'white',
          borderRadius: '25px',
          padding: '3rem',
          textAlign: 'center',
          boxShadow: '0 25px 50px rgba(0,0,0,0.2)'
        }}>
          <div style={{ fontSize: '4rem', marginBottom: '1rem' }}>⏳</div>
          <h2 style={{ color: 'var(--nigeria-green)' }}>Loading...</h2>
        </div>
      </div>
    );
  }

  return (
    <div className="order-success-page" style={{
      background: 'linear-gradient(to bottom, #f8f9fa, #ffffff)',
      minHeight: '100vh',
      paddingTop: '2rem',
      position: 'relative',
      overflow: 'hidden'
    }}>
      {/* Confetti Animation */}
      {showConfetti && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          pointerEvents: 'none',
          zIndex: 1000
        }}>
          {[...Array(50)].map((_, i) => (
            <div
              key={i}
              style={{
                position: 'absolute',
                top: `-10px`,
                left: `${Math.random() * 100}%`,
                width: '10px',
                height: '10px',
                background: ['#22c55e', '#fb923c', '#3b82f6', '#f59e0b', '#ef4444'][i % 5],
                borderRadius: '50%',
                animation: `confetti 3s ease-out forwards`,
                animationDelay: `${Math.random() * 2}s`,
                transform: `rotate(${Math.random() * 360}deg)`
              }}
            />
          ))}
        </div>
      )}

      <div className="container">
        {/* Success Header */}
        <div style={{
          background: 'linear-gradient(135deg, var(--nigeria-green), var(--warm-orange))',
          borderRadius: '25px',
          padding: '4rem 3rem',
          color: 'white',
          textAlign: 'center',
          marginBottom: '3rem',
          position: 'relative',
          overflow: 'hidden'
        }}>
          {/* Background Pattern */}
          <div style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            opacity: 0.1,
            backgroundImage: 'radial-gradient(circle at 30% 70%, white 3px, transparent 3px)',
            backgroundSize: '50px 50px',
            animation: 'drift 15s linear infinite'
          }}></div>

          <div style={{ position: 'relative', zIndex: 2 }}>
            {/* Success Icon */}
            <div style={{
              fontSize: '6rem',
              marginBottom: '2rem',
              animation: 'bounce 2s infinite',
              filter: 'drop-shadow(0 10px 20px rgba(0,0,0,0.3))'
            }}>
              🎉
            </div>
            
            <h1 style={{ 
              fontSize: '3.5rem',
              marginBottom: '1.5rem',
              fontWeight: '900',
              textShadow: '3px 3px 6px rgba(0,0,0,0.3)'
            }}>
              Order Confirmed!
            </h1>
            
            <p style={{ 
              fontSize: '1.4rem',
              marginBottom: '2rem',
              opacity: 0.95,
              maxWidth: '600px',
              margin: '0 auto 2rem',
              lineHeight: '1.6'
            }}>
              Thank you for choosing TiwaTinwa! Your delicious Nigerian meal is being prepared with love and will be delivered fresh to your doorstep.
            </p>

            {/* Order Number Badge */}
            <div style={{
              display: 'inline-block',
              background: 'rgba(255,255,255,0.2)',
              padding: '1.5rem 3rem',
              borderRadius: '25px',
              backdropFilter: 'blur(10px)',
              border: '2px solid rgba(255,255,255,0.3)',
              marginBottom: '2rem'
            }}>
              <div style={{
                fontSize: '1rem',
                opacity: 0.9,
                marginBottom: '0.5rem',
                fontWeight: '600'
              }}>
                Your Order Number
              </div>
              <div style={{
                fontSize: '2rem',
                fontWeight: '900',
                letterSpacing: '2px',
                fontFamily: 'monospace'
              }}>
                {orderData.orderId}
              </div>
            </div>

            {/* Quick Actions */}
            <div style={{
              display: 'flex',
              gap: '2rem',
              justifyContent: 'center',
              flexWrap: 'wrap',
              marginTop: '2rem'
            }}>
              <Link
                to={`/orders/${orderData.orderId}`}
                style={{
                  background: 'rgba(255,255,255,0.9)',
                  color: 'var(--nigeria-green)',
                  border: '2px solid white',
                  padding: '18px 35px',
                  borderRadius: '25px',
                  fontSize: '1.2rem',
                  fontWeight: '700',
                  cursor: 'pointer',
                  transition: 'all 0.3s ease',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.8rem',
                  textDecoration: 'none'
                }}
                onMouseOver={(e) => {
                  e.target.style.background = 'white';
                  e.target.style.transform = 'translateY(-4px) scale(1.05)';
                  e.target.style.boxShadow = '0 15px 35px rgba(255,255,255,0.4)';
                }}
                onMouseOut={(e) => {
                  e.target.style.background = 'rgba(255,255,255,0.9)';
                  e.target.style.transform = 'translateY(0) scale(1)';
                  e.target.style.boxShadow = 'none';
                }}
              >
                🚚 Track Your Order
              </Link>

              <button
                onClick={handleContinueShopping}
                style={{
                  background: 'rgba(255,255,255,0.15)',
                  color: 'white',
                  border: '2px solid rgba(255,255,255,0.8)',
                  padding: '18px 35px',
                  borderRadius: '25px',
                  fontSize: '1.2rem',
                  fontWeight: '700',
                  cursor: 'pointer',
                  transition: 'all 0.3s ease',
                  backdropFilter: 'blur(8px)',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.8rem'
                }}
                onMouseOver={(e) => {
                  e.target.style.background = 'rgba(255,255,255,0.25)';
                  e.target.style.borderColor = 'rgba(255,255,255,1)';
                  e.target.style.transform = 'translateY(-4px) scale(1.05)';
                  e.target.style.boxShadow = '0 15px 35px rgba(255,255,255,0.2)';
                }}
                onMouseOut={(e) => {
                  e.target.style.background = 'rgba(255,255,255,0.15)';
                  e.target.style.borderColor = 'rgba(255,255,255,0.8)';
                  e.target.style.transform = 'translateY(0) scale(1)';
                  e.target.style.boxShadow = 'none';
                }}
              >
                🍽️ Continue Shopping
              </button>
            </div>
          </div>
        </div>

        {/* Order Details & Delivery Info */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: '1fr 1fr',
          gap: '3rem',
          marginBottom: '3rem'
        }}>
          {/* Order Summary */}
          <div style={{
            background: 'white',
            borderRadius: '25px',
            padding: '3rem',
            boxShadow: '0 15px 40px rgba(0,0,0,0.1)',
            border: '2px solid #f0f0f0'
          }}>
            <h2 style={{
              fontSize: '2rem',
              color: 'var(--nigeria-green)',
              marginBottom: '2rem',
              fontWeight: '800',
              textAlign: 'center'
            }}>
              📋 Order Summary
            </h2>

            <div style={{ marginBottom: '2rem' }}>
              <h3 style={{
                fontSize: '1.3rem',
                color: 'var(--nigeria-green)',
                marginBottom: '1.5rem',
                fontWeight: '700'
              }}>
                🍽️ Items Ordered:
              </h3>
              
              {orderData.items.map((item, index) => (
                <div key={index} style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  padding: '1.5rem',
                  background: 'linear-gradient(135deg, #f8f9fa, #ffffff)',
                  borderRadius: '20px',
                  marginBottom: '1rem',
                  border: '2px solid #e9ecef',
                  transition: 'all 0.3s ease'
                }}>
                  <div style={{ flex: 1 }}>
                    <div style={{
                      fontSize: '1.1rem',
                      fontWeight: '700',
                      color: 'var(--text-dark)',
                      marginBottom: '0.5rem'
                    }}>
                      {item.name}
                    </div>
                    <div style={{
                      fontSize: '0.9rem',
                      color: 'var(--text-light)',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '1rem'
                    }}>
                      <span>Quantity: {item.quantity}</span>
                      <span>•</span>
                      <span>₦{item.price.toLocaleString()} each</span>
                    </div>
                  </div>
                  <div style={{
                    fontSize: '1.2rem',
                    fontWeight: '900',
                    color: 'var(--nigeria-green)'
                  }}>
                    ₦{(item.price * item.quantity).toLocaleString()}
                  </div>
                </div>
              ))}
            </div>

            <div style={{
              marginTop: '2rem',
              paddingTop: '2rem',
              borderTop: '3px solid var(--nigeria-green)'
            }}>
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginBottom: '1rem'
              }}>
                <span style={{
                  fontSize: '1.1rem',
                  fontWeight: '600',
                  color: 'var(--text-dark)'
                }}>
                  Subtotal:
                </span>
                <span style={{
                  fontSize: '1.1rem',
                  fontWeight: '700',
                  color: 'var(--text-dark)'
                }}>
                  ₦{orderData.total.toLocaleString()}
                </span>
              </div>
              
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginBottom: '1rem'
              }}>
                <span style={{
                  fontSize: '1.1rem',
                  fontWeight: '600',
                  color: 'var(--text-dark)'
                }}>
                  Delivery Fee:
                </span>
                <span style={{
                  fontSize: '1.1rem',
                  fontWeight: '700',
                  color: 'var(--nigeria-green)'
                }}>
                  FREE 🎉
                </span>
              </div>

              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                padding: '1.5rem',
                background: 'linear-gradient(135deg, var(--nigeria-green), var(--warm-orange))',
                borderRadius: '20px',
                color: 'white'
              }}>
                <span style={{
                  fontSize: '1.4rem',
                  fontWeight: '800'
                }}>
                  Total Paid:
                </span>
                <span style={{
                  fontSize: '1.8rem',
                  fontWeight: '900',
                  textShadow: '2px 2px 4px rgba(0,0,0,0.3)'
                }}>
                  ₦{orderData.total.toLocaleString()}
                </span>
              </div>
            </div>
          </div>

          {/* Delivery Information */}
          <div style={{
            background: 'white',
            borderRadius: '25px',
            padding: '3rem',
            boxShadow: '0 15px 40px rgba(0,0,0,0.1)',
            border: '2px solid #f0f0f0'
          }}>
            <h2 style={{
              fontSize: '2rem',
              color: 'var(--nigeria-green)',
              marginBottom: '2rem',
              fontWeight: '800',
              textAlign: 'center'
            }}>
              🚚 Delivery Details
            </h2>

            {/* Estimated Delivery Time */}
            <div style={{
              background: 'linear-gradient(135deg, #22c55e, #16a34a)',
              borderRadius: '20px',
              padding: '2rem',
              color: 'white',
              textAlign: 'center',
              marginBottom: '2rem',
              position: 'relative',
              overflow: 'hidden'
            }}>
              <div style={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                opacity: 0.1,
                backgroundImage: 'radial-gradient(circle at 50% 50%, white 2px, transparent 2px)',
                backgroundSize: '30px 30px'
              }}></div>

              <div style={{ position: 'relative', zIndex: 2 }}>
                <div style={{
                  fontSize: '3rem',
                  marginBottom: '1rem',
                  animation: 'pulse 2s infinite'
                }}>
                  ⏰
                </div>
                <h3 style={{
                  fontSize: '1.5rem',
                  fontWeight: '700',
                  marginBottom: '0.5rem',
                  textShadow: '1px 1px 2px rgba(0,0,0,0.3)'
                }}>
                  Estimated Delivery
                </h3>
                <p style={{
                  fontSize: '1.8rem',
                  fontWeight: '900',
                  margin: 0,
                  textShadow: '2px 2px 4px rgba(0,0,0,0.3)'
                }}>
                  {orderData.estimatedDelivery.toLocaleTimeString([], {
                    hour: '2-digit',
                    minute: '2-digit'
                  })}
                </p>
                <p style={{
                  fontSize: '1rem',
                  opacity: 0.9,
                  margin: '0.5rem 0 0',
                  fontWeight: '600'
                }}>
                  (30-45 minutes from now)
                </p>
              </div>
            </div>

            {/* Delivery Timeline */}
            <div style={{ marginBottom: '2rem' }}>
              <h4 style={{
                fontSize: '1.3rem',
                color: 'var(--nigeria-green)',
                marginBottom: '1.5rem',
                fontWeight: '700'
              }}>
                📅 What happens next:
              </h4>

              {[
                {
                  time: '2-5 min',
                  status: 'Order Confirmation',
                  description: 'We\'ll confirm your order and send it to our kitchen',
                  icon: '✅',
                  active: true
                },
                {
                  time: '15-25 min',
                  status: 'Food Preparation',
                  description: 'Our chefs are preparing your delicious Nigerian meal',
                  icon: '👨‍🍳',
                  active: false
                },
                {
                  time: '30-45 min',
                  status: 'Out for Delivery',
                  description: 'Your order is on its way to you',
                  icon: '🚚',
                  active: false
                }
              ].map((step, index) => (
                <div key={index} style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '1rem',
                  padding: '1rem',
                  background: step.active ? 'rgba(34, 197, 94, 0.1)' : '#f8f9fa',
                  borderRadius: '15px',
                  marginBottom: '1rem',
                  border: step.active ? '2px solid #22c55e' : '2px solid #e9ecef',
                  transition: 'all 0.3s ease'
                }}>
                  <div style={{
                    fontSize: '2rem',
                    filter: step.active ? 'none' : 'grayscale(100%)',
                    opacity: step.active ? 1 : 0.6
                  }}>
                    {step.icon}
                  </div>
                  <div style={{ flex: 1 }}>
                    <div style={{
                      fontSize: '1rem',
                      fontWeight: '700',
                      color: step.active ? 'var(--nigeria-green)' : 'var(--text-dark)',
                      marginBottom: '0.3rem'
                    }}>
                      {step.status}
                    </div>
                    <div style={{
                      fontSize: '0.9rem',
                      color: 'var(--text-light)'
                    }}>
                      {step.description}
                    </div>
                  </div>
                  <div style={{
                    fontSize: '0.8rem',
                    fontWeight: '600',
                    color: step.active ? 'var(--nigeria-green)' : 'var(--text-light)',
                    padding: '0.3rem 0.8rem',
                    background: step.active ? 'rgba(34, 197, 94, 0.2)' : 'rgba(0,0,0,0.05)',
                    borderRadius: '10px'
                  }}>
                    {step.time}
                  </div>
                </div>
              ))}
            </div>

            {/* Auto-redirect Notice */}
            <div style={{
              background: 'linear-gradient(135deg, #3b82f6, #2563eb)',
              borderRadius: '15px',
              padding: '1.5rem',
              color: 'white',
              textAlign: 'center'
            }}>
              <div style={{
                fontSize: '1.5rem',
                marginBottom: '0.5rem'
              }}>
                🔄
              </div>
              <p style={{
                fontSize: '0.9rem',
                fontWeight: '600',
                margin: 0,
                opacity: 0.9
              }}>
                Redirecting to order tracking in {countdown} seconds...
              </p>
            </div>
          </div>
        </div>

        {/* Customer Support Section */}
        <div style={{
          background: 'linear-gradient(135deg, var(--warm-orange), #ff8c42)',
          borderRadius: '25px',
          padding: '3rem',
          color: 'white',
          textAlign: 'center',
          position: 'relative',
          overflow: 'hidden'
        }}>
          <div style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            opacity: 0.1,
            backgroundImage: 'radial-gradient(circle at 25% 75%, white 2px, transparent 2px)',
            backgroundSize: '40px 40px',
            animation: 'drift 10s linear infinite'
          }}></div>

          <div style={{ position: 'relative', zIndex: 2 }}>
            <h2 style={{
              fontSize: '2.5rem',
              marginBottom: '1.5rem',
              fontWeight: '800',
              textShadow: '2px 2px 4px rgba(0,0,0,0.3)'
            }}>
              🤝 Need Help?
            </h2>
            
            <p style={{
              fontSize: '1.2rem',
              marginBottom: '2rem',
              opacity: 0.95,
              maxWidth: '600px',
              margin: '0 auto 2rem'
            }}>
              Our customer support team is here to help you with any questions about your order.
            </p>

            <div style={{
              display: 'flex',
              gap: '2rem',
              justifyContent: 'center',
              flexWrap: 'wrap'
            }}>
              <a
                href="tel:+2348012345678"
                style={{
                  background: 'rgba(255,255,255,0.9)',
                  color: 'var(--warm-orange)',
                  textDecoration: 'none',
                  padding: '15px 30px',
                  borderRadius: '20px',
                  fontSize: '1.1rem',
                  fontWeight: '700',
                  transition: 'all 0.3s ease',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem'
                }}
                onMouseOver={(e) => {
                  e.target.style.background = 'white';
                  e.target.style.transform = 'translateY(-3px) scale(1.05)';
                  e.target.style.boxShadow = '0 10px 25px rgba(255,255,255,0.3)';
                }}
                onMouseOut={(e) => {
                  e.target.style.background = 'rgba(255,255,255,0.9)';
                  e.target.style.transform = 'translateY(0) scale(1)';
                  e.target.style.boxShadow = 'none';
                }}
              >
                📞 Call Us
              </a>

              <Link
                to="/contact"
                style={{
                  background: 'rgba(255,255,255,0.15)',
                  color: 'white',
                  textDecoration: 'none',
                  border: '2px solid rgba(255,255,255,0.8)',
                  padding: '15px 30px',
                  borderRadius: '20px',
                  fontSize: '1.1rem',
                  fontWeight: '700',
                  transition: 'all 0.3s ease',
                  backdropFilter: 'blur(8px)',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem'
                }}
                onMouseOver={(e) => {
                  e.target.style.background = 'rgba(255,255,255,0.25)';
                  e.target.style.transform = 'translateY(-3px) scale(1.05)';
                  e.target.style.boxShadow = '0 10px 25px rgba(255,255,255,0.2)';
                }}
                onMouseOut={(e) => {
                  e.target.style.background = 'rgba(255,255,255,0.15)';
                  e.target.style.transform = 'translateY(0) scale(1)';
                  e.target.style.boxShadow = 'none';
                }}
              >
                💬 Chat Support
              </Link>
            </div>
          </div>
        </div>
      </div>

      {/* CSS Animations */}
      <style jsx>{`
        @keyframes confetti {
          0% {
            opacity: 1;
            transform: translateY(-10px) rotate(0deg);
          }
          100% {
            opacity: 0;
            transform: translateY(100vh) rotate(720deg);
          }
        }
        
        @keyframes bounce {
          0%, 20%, 50%, 80%, 100% { transform: translateY(0); }
          40% { transform: translateY(-20px); }
          60% { transform: translateY(-10px); }
        }
        
        @keyframes drift {
          0% { transform: translateX(0); }
          100% { transform: translateX(30px); }
        }

        @keyframes pulse {
          0%, 100% { transform: scale(1); opacity: 1; }
          50% { transform: scale(1.1); opacity: 0.8; }
        }

        /* Mobile Responsiveness */
        @media (max-width: 768px) {
          .container {
            padding: 0 1rem !important;
          }
          
          .order-details-grid {
            grid-template-columns: 1fr !important;
            gap: 2rem !important;
          }
          
          .success-header h1 {
            font-size: 2.5rem !important;
          }
          
          .action-buttons {
            flex-direction: column !important;
            align-items: center !important;
          }
        }
      `}</style>
    </div>
  );
};

export default OrderSuccessPage;